import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import PageTitle from '../shared/PageTitle';
import PricingPlans from '../billing/PricingPlans';

// Fox mascot images
const foxOnStump = '/images/foxonstump.png';
const foxAndTreasureMap = '/images/foxandtreasuremap.png';
const foxAndQuill = '/images/foxandquill.png';
const foxAndBanner = '/images/foxandbanner.png';
const library = '/images/library.png';
import './Landing.css';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

export default function Landing() {
  useEffect(() => {
    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          name: 'Fox & Quill',
          url: 'https://foxandquill.art',
          description: 'The storybook workshop where kids become real authors. Children write tales, paint illustrations, add stamps, and turn their ideas into beautiful printed books.',
        },
        {
          '@type': 'Organization',
          name: 'Fox & Quill',
          url: 'https://foxandquill.art',
          logo: 'https://foxandquill.art/images/foxandquill.png',
          sameAs: [],
        },
        {
          '@type': 'WebApplication',
          name: 'Fox & Quill',
          url: 'https://foxandquill.art',
          applicationCategory: 'EducationalApplication',
          operatingSystem: 'Web',
          offers: {
            '@type': 'AggregateOffer',
            lowPrice: '0',
            highPrice: '16.99',
            priceCurrency: 'USD',
          },
          description: 'A creative writing and bookmaking platform for children ages 4-8. Kids write, illustrate, and publish their own printed storybooks.',
          audience: {
            '@type': 'EducationalAudience',
            educationalRole: 'student',
            suggestedMinAge: 4,
            suggestedMaxAge: 8,
          },
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="landing">
      <PageTitle title="" />
      <Navbar />

      {/* Hero */}
      <section className="hero" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/forestbackground.png)` }}>
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="hero-title">
            The storybook workshop<br />
            where kids become<br />
            <span className="hero-highlight">real authors</span>
          </h1>
          <p className="hero-subtitle">
            Fox &amp; Quill lets children write tales, paint illustrations, add stamps,
            and turn their biggest ideas into beautiful books they can share with the
            world or hold in their hands.
          </p>
          <div className="hero-cta">
            <Link to="/create" className="btn btn-primary btn-large">
              Begin your tale
            </Link>
            <p className="hero-sub-cta">No sign-up needed. Create your first book in minutes.</p>
          </div>
        </motion.div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="how-it-works">
        <div className="container">
          <motion.div {...fadeUp} className="section-header">
            <h2>How it works</h2>
            <p>From imagination to bookshelf in minutes.</p>
          </motion.div>

          <div className="steps-with-fox">
            <motion.div
              className="steps-fox"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img src={foxAndTreasureMap} alt="Fox holding a treasure map" className="section-fox-image" />
            </motion.div>
            <motion.div
              className="steps-list"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              {[
                { num: '1', title: 'Name your tale', desc: 'Pick a title and choose a cover color. Your book appears on the shelf, ready and waiting.' },
                { num: '2', title: 'Create your pages', desc: 'Write, draw, add photos and stamps. Every page is a canvas. There are no rules except the ones you invent.' },
                { num: '3', title: 'Share your tale', desc: "When you're ready, share a link with anyone. Your tale gets its own page that works on any device." },
                { num: '4', title: 'Hold it in your hands', desc: 'Order a printed copy, hardcover or softcover, delivered to your door. A real book with their name on the cover.' },
              ].map((step, i) => (
                <div key={i} className="step">
                  <div className="step-number">{step.num}</div>
                  <div className="step-content">
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="features">
        <div className="container">
          <motion.div {...fadeUp} className="section-header">
            <h2>Everything a young author needs</h2>
            <p>From imagination to bookshelf, one page at a time.</p>
          </motion.div>

          <div className="features-with-fox">
            <motion.div
              className="features-list"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {[
                { bold: 'Write tales', rest: 'Beautiful fonts that feel like real handwriting. Type or dictate.' },
                { bold: 'Draw and paint', rest: 'Colorful brushes on a full canvas. Illustrate every page.' },
                { bold: 'Add photos', rest: 'Upload drawings, photos, or artwork from the real world.' },
                { bold: 'Stamps', rest: 'Hundreds of hand-drawn stamps. Drag, place, and make each page yours.' },
                { bold: 'Real book feel', rest: 'Paper texture, page curls, and beautiful typography. Not just another app.' },
                { bold: 'Share your tale', rest: 'Send a link to grandparents, friends, or teachers. Works on any device.' },
              ].map((feature, i) => (
                <div key={i} className="feature-bullet">
                  <Star size={16} fill="#D4A842" stroke="#D4A842" />
                  <span><strong>{feature.bold}</strong> {feature.rest}</span>
                </div>
              ))}
            </motion.div>
            <motion.div
              className="features-fox"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <img src={foxAndQuill} alt="Fox with a quill pen" className="section-fox-image" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="pricing" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/green-background.png)` }}>
        <div className="container">
          <motion.div {...fadeUp} className="section-header">
            <h2>Plans for every kind of author</h2>
            <p>Begin free, grow when you're ready.</p>
          </motion.div>

          <PricingPlans mode="landing" />
          <p className="pricing-caption">Every plan includes printed books. Because the best stories deserve to be held.</p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/flower-background.png)` }}>
        <div className="container">
          <motion.div {...fadeUp} className="section-header">
            <h2>Stories they can hold</h2>
          </motion.div>
          <div className="testimonials-grid">
            {[
              { quote: 'My 6-year-old made a book about her cat and we ordered a printed copy. She shows it to everyone.', name: 'Sarah M.', detail: 'Mom of 1' },
              { quote: "It's truly a screen time activity I feel good about. My kids are writing and creating instead of just watching.", name: 'David L.', detail: 'Dad of 3' },
              { quote: 'We sent Grandma a link to our son\'s book and she cried happy tears. Best gift ever.', name: 'Jessica T.', detail: 'Mom of 1' },
            ].map((t, i) => (
              <motion.div
                key={i}
                className="testimonial-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="testimonial-stars">
                  <Star size={14} fill="#D4A842" stroke="#D4A842" />
                  <Star size={14} fill="#D4A842" stroke="#D4A842" />
                  <Star size={14} fill="#D4A842" stroke="#D4A842" />
                  <Star size={14} fill="#D4A842" stroke="#D4A842" />
                  <Star size={14} fill="#D4A842" stroke="#D4A842" />
                </div>
                <p className="testimonial-quote">"{t.quote}"</p>
                <div className="testimonial-author">
                  <strong>{t.name}</strong>
                  <span>{t.detail}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <motion.div {...fadeUp} className="cta-layout">
            <img src={foxOnStump} alt="Fox sitting on a stump" className="cta-side-image cta-image-left" />
            <div className="cta-content">
              <h2>From imagination to bookshelf</h2>
              <p>Give your child the joy of creating something real. Begin for free today.</p>
              <Link to="/create" className="btn btn-primary btn-large">
                Begin creating now, free
              </Link>
            </div>
            <img src={library} alt="Library bookshelf" className="cta-side-image cta-image-right" />
          </motion.div>
        </div>
      </section>

      <Footer navLinks={[
        { label: 'Features', href: '#features' },
        { label: 'Pricing', href: '#pricing' },
        { label: 'Blog', to: '/blog' },
        { label: 'Log In', to: '/login' },
        { label: 'Sign Up', to: '/signup' },
      ]} />
    </div>
  );
}
